import { useCallback, useEffect, useRef, useState } from 'react';
import { CHIP_DENOMS } from '@tv-casino/shared';
import styles from './ChipBet.module.css';

const DENOMS = [...CHIP_DENOMS].reverse();
const TAP_SLOP_PX = 8;

/** Break a total into house chips, largest first, with a short chip for the remainder. */
const pileOf = (amount: number): number[] => {
  const chips: number[] = [];
  let left = Math.max(0, Math.floor(amount));
  for (const denom of DENOMS) {
    while (left >= denom) {
      chips.push(denom);
      left -= denom;
    }
  }
  if (left > 0) chips.push(left);
  return chips;
};

const chipClass = (chip: number) =>
  chip >= 500 ? styles.chip500 : chip >= 100 ? styles.chip100 : chip >= 25 ? styles.chip25 : styles.chip5;

interface Ghost {
  chip: number;
  x: number;
  y: number;
  over: boolean;
}

/**
 * Betting board for the phone: drag chips from the tray into the bet circle
 * (a quick tap stages one too). Chips stack up locally; "Bet" sends the total
 * as a single player_bet, exactly like the old tap buttons did. Tap the stack
 * to take the top chip back, or Clear to sweep it.
 */
export function ChipBet({
  chips,
  busy,
  minBet = 5,
  extra = 0,
  allowShort = false,
  fill = null,
  onStaged,
  placeLabel,
  onPlace,
}: {
  /** Authoritative stack still available to bet. */
  chips: number;
  busy: boolean;
  /** The table's minimum, from room rules. */
  minBet?: number;
  /** Side/pair bets already staged elsewhere; ride along on the same Bet press. */
  extra?: number;
  /**
   * A chip bigger than what's left still drops, and puts the rest in.
   * Hold'em needs it: a short stack and a pot-limit cap are not house
   * denominations. Other games leave it off so a min-bet stays a whole chip.
   */
  allowShort?: boolean;
  /**
   * Drop this many chips into the circle. A new `id` replaces the pile,
   * which is how Hold'em's Min / Pot / All-in buttons fill the tray.
   */
  fill?: { id: number; amount: number } | null;
  /** Fires when the pile total changes, including a fill. */
  onStaged?: (staged: number) => void;
  /** Replaces "Bet {total}". Receives the pile in the circle, not `extra`. */
  placeLabel?: (staged: number) => string;
  /** Resolves true once the table has taken the wager; false if it refused. */
  onPlace: (total: number) => Promise<boolean>;
}) {
  const [stack, setStack] = useState<number[]>([]);
  const [ghost, setGhost] = useState<Ghost | null>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ chip: number; startX: number; startY: number } | null>(null);

  const staged = stack.reduce((sum, chip) => sum + chip, 0);
  const committed = staged + extra;

  useEffect(() => {
    if (!fill) return;
    const capped = Math.min(Math.max(0, Math.floor(fill.amount)), Math.max(0, chips));
    setStack(pileOf(capped));
  }, [fill, chips]);

  useEffect(() => {
    onStaged?.(staged);
  }, [staged, onStaged]);
  const remaining = chips - staged;
  const canAdd = (chip: number) => chip <= remaining || (allowShort && remaining > 0);

  const addChip = useCallback(
    (chip: number) =>
      setStack((s) => {
        const have = s.reduce((sum, c) => sum + c, 0);
        const room = chips - have;
        if (room <= 0) return s;
        if (chip <= room) return [...s, chip];
        return allowShort ? [...s, room] : s;
      }),
    [allowShort, chips],
  );

  const overCircle = (x: number, y: number) => {
    const r = circleRef.current?.getBoundingClientRect();
    return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  const beginDrag = (chip: number, e: React.PointerEvent<HTMLButtonElement>) => {
    if (busy || !canAdd(chip)) return;
    e.preventDefault();
    drag.current = { chip, startX: e.clientX, startY: e.clientY };
    setGhost({ chip, x: e.clientX, y: e.clientY, over: false });

    const teardown = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', teardown);
      drag.current = null;
      setGhost(null);
    };
    const move = (ev: PointerEvent) => {
      if (!drag.current) return teardown();
      setGhost({
        chip,
        x: ev.clientX,
        y: ev.clientY,
        over: overCircle(ev.clientX, ev.clientY),
      });
    };
    const end = (ev: PointerEvent) => {
      const d = drag.current;
      teardown();
      if (!d) return;
      const traveled = Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY);
      // A drop on the circle or a plain tap both stage the chip.
      if (overCircle(ev.clientX, ev.clientY) || traveled < TAP_SLOP_PX) addChip(d.chip);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', teardown);
  };

  // Only the last few chips read visually; the total line carries the number.
  const visible = stack.slice(-10);

  return (
    <div className={styles.board}>
      <div
        ref={circleRef}
        className={`${styles.circle} ${ghost?.over ? styles.circleHot : ''}`}
      >
        {stack.length === 0 ? (
          <span className={styles.hint}>Drag chips here</span>
        ) : (
          <button
            type="button"
            className={styles.stack}
            onClick={() => setStack((s) => s.slice(0, -1))}
            aria-label={`Bet ${staged}. Tap to take back the last chip.`}
          >
            <span className={styles.stackChips}>
              {visible.map((chip, i) => (
                <span
                  key={`${i}-${chip}`}
                  className={`${styles.stackChip} ${chipClass(chip)}`}
                  style={{ transform: `translateY(${i * -5}px)` }}
                >
                  {chip}
                </span>
              ))}
            </span>
          </button>
        )}
        {stack.length > 0 && <span className={styles.total}>{staged.toLocaleString()}</span>}
      </div>

      <div className={styles.tray} role="group" aria-label="Chip tray">
        {DENOMS.map((chip) => (
          <button
            key={chip}
            type="button"
            className={`${styles.trayChip} ${chipClass(chip)} ${canAdd(chip) ? '' : styles.chipOff}`}
            disabled={busy || !canAdd(chip)}
            onPointerDown={(e) => beginDrag(chip, e)}
          >
            {chip}
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.clear}
          disabled={busy || stack.length === 0}
          onClick={() => setStack([])}
        >
          Clear
        </button>
        <button
          type="button"
          className={styles.place}
          disabled={busy || staged < minBet}
          // The pile stays in the circle until the table takes it. A refused
          // bet used to sweep the chips anyway, so a phone that woke to a
          // dead socket read as "the app ate my wager".
          onClick={() => {
            void onPlace(staged).then((ok) => {
              if (ok) setStack([]);
            });
          }}
        >
          {placeLabel ? placeLabel(staged) : `Bet ${committed.toLocaleString()}`}
        </button>
      </div>

      {ghost && (
        <span
          className={`${styles.ghost} ${chipClass(ghost.chip)}`}
          style={{ left: ghost.x, top: ghost.y }}
        >
          {ghost.chip}
        </span>
      )}
    </div>
  );
}
